import React from 'react';
import {
    User, Bell, Calendar, FileText, LifeBuoy,
    ChevronRight, Clock, CheckCircle, AlertCircle,
    MessageSquare,
    // Link,
    ExternalLink
} from 'lucide-react';
import { useGetCustomerDashboardStats } from '../../custom-hooks/use-stats';
import { useGetUserNotifications } from '../../custom-hooks/use-notifications';
import { useMarkNotificationRead } from '../../custom-hooks/use-notifications';
import { convertToPresentableText } from '../../utils/functions';
import moment from 'moment';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Spinner } from '@material-tailwind/react';
import { URLS } from '../../router-x/routes';
import AnnouncementModal from './AnnouncementModal';
import PortalHomeHeader from '../../components/portal-home-header';

const QuickLink = ({ to, icon, title, subtitle }) => (
    <RouterLink
        to={to}
        className="flex items-center gap-3.5 rounded-2xl border border-[#ECE3D5] bg-white p-[18px] transition-all hover:-translate-y-0.5 hover:shadow-[0_16px_34px_-20px_rgba(40,22,8,0.4)]"
    >
        <div className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
            {icon}
        </div>
        <div className="min-w-0 flex-1">
            <div className="font-display text-[15px] font-semibold text-ink">{title}</div>
            <div className="mt-0.5 text-[12.5px] text-[#8C8175]">{subtitle}</div>
        </div>
        <ExternalLink size={18} className="shrink-0 text-[#C5B9AA]" />
    </RouterLink>
);

const CustomerDashboard = () => {

    const { data: stats, isLoading, error } = useGetCustomerDashboardStats();
    const { data: notificationsResponse } = useGetUserNotifications();
    const markNotificationReadMutation = useMarkNotificationRead();
    const [showAllBookings, setShowAllBookings] = React.useState(false);
    const [showAllNotifications, setShowAllNotifications] = React.useState(false);
    const [clearedNotificationIds, setClearedNotificationIds] = React.useState([]);
    const navigate = useNavigate();

    const handleNotificationClick = (notification) => {
        let url = notification.actionUrl;
        if (url) {
            // Handle legacy URLs
            if (url.startsWith('/admin/tickets')) url = url.replace('/admin/tickets', '/support');
            if (url.startsWith('/tickets')) url = url.replace('/tickets', '/support');

            // Map support to customer portal
            if (url.startsWith('/support')) url = `/c${url}`;

            if (!notification.readAt) {
                markNotificationReadMutation.mutate({ notificationId: notification._id || notification.id });
            }
            navigate(url);
        }
    };

    const notifications = notificationsResponse?.notifications || stats?.notifications || [];
    const dashboardNotifications = notifications.filter((n) => {
        const id = n?._id || n?.id;
        return !n?.readAt && n?.status !== 'read' && !clearedNotificationIds.includes(id);
    });
    const unreadNotificationsCount = dashboardNotifications.length;

    const clearNotification = async (notificationId) => {
        if (!notificationId) return;
        setClearedNotificationIds((prev) => [...prev, notificationId]);
        try {
            await markNotificationReadMutation.mutateAsync({ notificationId });
        } catch (e) {
            // Revert local clear state if backend update fails.
            setClearedNotificationIds((prev) => prev.filter((id) => id !== notificationId));
        }
    };

    const clearAllNotifications = async () => {
        const idsToClear = dashboardNotifications
            .map((n) => n?._id || n?.id)
            .filter(Boolean);
        if (idsToClear.length === 0) return;

        setClearedNotificationIds((prev) => [...new Set([...prev, ...idsToClear])]);
        try {
            await Promise.all(idsToClear.map((id) => markNotificationReadMutation.mutateAsync({ notificationId: id })));
        } catch (e) {
            // If any request fails, refresh from backend state via query invalidation in hook.
        }
    };

    if (isLoading) {
        return <div className='flex min-h-[60vh] items-center justify-center'>
            <Spinner className="h-8 w-8" color="orange" />
        </div>;
    }

    if (error) {
        return <div className="px-6 py-10 text-center text-[#8C8175]">Error: {error.message}</div>;
    }

    if (stats && stats.length === 0) {
        return <div className="px-6 py-10 text-center text-[#8C8175]">No stats found</div>;
    }

    const quotaUsed = stats.meetingQuota?.used;
    const quotaTotal = stats.meetingQuota?.total;
    const remainingPct = quotaTotal ? parseInt(100 - ((quotaUsed / quotaTotal) * 100)) : 0;

    return (
        <div className="animate-fadeIn px-4 py-5 md:px-[34px] md:py-[30px]">
                <PortalHomeHeader name={convertToPresentableText(stats.customerId?.name || '')} />

                {/* Quick Links */}
                <div className="mb-[18px] grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <QuickLink to={URLS.CUSTOMER.PROFILE} icon={<User size={24} />} title="Profile" subtitle="Manage your account" />
                    <QuickLink to={URLS.CUSTOMER.MEETING_ROOM} icon={<Calendar size={24} />} title="Meeting Rooms" subtitle="Book a room" />
                    <QuickLink to={URLS.CUSTOMER.CONTRACT} icon={<FileText size={24} />} title="Contracts" subtitle="View your agreements" />
                    <QuickLink to={URLS.CUSTOMER.SUPPORT} icon={<LifeBuoy size={24} />} title="Support" subtitle="Contact us" />
                </div>

                {/* Meeting Quota Card */}
                <div className="mb-[18px] rounded-2xl border border-[#ECE3D5] bg-white p-6">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-4">
                            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cream-input text-brand">
                                <Clock size={24} />
                            </div>
                            <div>
                                <h3 className="font-display text-base font-semibold text-ink">Meeting Room Quota</h3>
                                <p className="text-sm text-[#8C8175]">{stats.meetingQuota?.remaining / 60} of {stats.meetingQuota?.total / 60} hours remaining this month</p>
                            </div>
                        </div>

                        <RouterLink
                            to={URLS.CUSTOMER.MEETING_ROOM}
                            className="inline-flex w-full items-center justify-center rounded-xl bg-brand px-6 py-2.5 font-display text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-brand-dark hover:shadow-[0_12px_24px_-12px_rgba(190,83,16,0.8)] md:w-auto"
                        >
                            Book a Room
                        </RouterLink>
                    </div>

                    <div className="mt-5">
                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-cream-100">
                            <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${remainingPct}%` }} />
                        </div>
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-1">
                            <p className="text-xs text-[#8C8175]">
                                Quota resets on the 1st of each month
                                {' '}· Last reset: {stats.meetingQuota?.lastResetDate ? moment(stats.meetingQuota?.lastResetDate).format('DD MMM YYYY') : 'Never'}
                            </p>
                            <span className="text-xs font-semibold text-brand">{remainingPct}% remaining</span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                    {/* Upcoming Bookings */}
                    <section className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-display text-base font-semibold text-ink">Upcoming Bookings</h2>
                            <RouterLink to={URLS.CUSTOMER.MEETING_ROOM} className="flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
                                View all <ChevronRight className="h-4 w-4" />
                            </RouterLink>
                        </div>
                        {stats.upcomingMeetingRoomBookings?.length > 0 ? (
                            <div className="space-y-4">
                                {stats.upcomingMeetingRoomBookings.slice(0, showAllBookings ? undefined : 3).map(booking => {
                                    return (
                                        <div key={booking._id} className="rounded-xl border border-[#ECE3D5] p-4 transition-colors hover:bg-cream">
                                            <div className="flex items-start gap-3">
                                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream-input text-brand">
                                                    <Clock className="h-5 w-5" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <h3 className="font-medium text-ink">{booking.meetingRoom?.name}</h3>
                                                    <p className="text-sm text-[#8C8175]">{booking.meetingRoom?.location}</p>
                                                    <div className="mt-1.5 flex items-center gap-2 text-sm text-[#8C8175]">
                                                        <span>{moment(booking.startDate).format('DD MMM YYYY')}</span>
                                                        <span>•</span>
                                                        <span>{moment(booking.startDate).format('h:mm A')} - {moment(booking.endDate).format('h:mm A')}</span>
                                                    </div>
                                                </div>
                                                <RouterLink
                                                    to={`${URLS.CUSTOMER.MEETING_ROOM}/booking/${booking._id}`}
                                                    className="shrink-0 rounded-lg bg-cream-100 px-2.5 py-1 text-xs font-medium text-[#6B675F] transition-colors hover:bg-cream-input"
                                                >
                                                    Details
                                                </RouterLink>
                                            </div>
                                        </div>
                                    )
                                })}
                                {stats.upcomingMeetingRoomBookings.length > 3 && (
                                    <div className="text-center">
                                        <button
                                            onClick={() => setShowAllBookings(!showAllBookings)}
                                            className="text-sm font-semibold text-brand hover:underline"
                                        >
                                            {showAllBookings ? 'Show Less' : `Show ${stats.upcomingMeetingRoomBookings.length - 3} More`}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-8 text-center">
                                <Clock className="mx-auto mb-3 h-12 w-12 text-[#D8CDBD]" />
                                <p className="text-[#8C8175]">No upcoming bookings</p>
                                <RouterLink
                                    to={URLS.CUSTOMER.MEETING_ROOM}
                                    className="mt-2 inline-block text-sm font-semibold text-brand hover:underline"
                                >
                                    Book a meeting room
                                </RouterLink>
                            </div>
                        )}
                    </section>

                    {/* Notifications */}
                    <section className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <h2 className="font-display text-base font-semibold text-ink">Notifications</h2>
                                <span className="rounded-full bg-cream-input px-2.5 py-0.5 text-xs font-semibold text-brand">
                                    {unreadNotificationsCount > 0 ? `${unreadNotificationsCount} new` : 'No new notifications'}
                                </span>
                            </div>
                            {dashboardNotifications.length > 0 && (
                                <button
                                    onClick={clearAllNotifications}
                                    disabled={markNotificationReadMutation.isLoading}
                                    className="text-sm font-medium text-[#6B675F] transition-colors hover:text-brand disabled:opacity-50"
                                >
                                    Clear all
                                </button>
                            )}
                        </div>

                        {dashboardNotifications.length > 0 ? (
                            <div className="space-y-3">
                                {dashboardNotifications.slice(0, showAllNotifications ? undefined : 3).map(notification => {
                                    const isWarn = notification.notificationType === 'sla_breach_warning' || notification.notificationType === 'escalation_alert';
                                    const isInfo = notification.notificationType === 'new_message' || notification.notificationType === 'ticket_created' || notification.notificationType === 'agent_assigned';
                                    return (
                                        <div
                                            key={notification._id || notification.id}
                                            onClick={() => handleNotificationClick(notification)}
                                            className={`rounded-xl p-3 transition-colors ${notification.actionUrl ? 'cursor-pointer' : ''} ${notification.readAt ? 'bg-cream' : 'border-l-4 border-brand bg-cream-input/50 hover:bg-cream-input'}`}
                                        >
                                            <div className="flex justify-between gap-3">
                                                <div className="flex items-start gap-3">
                                                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${isWarn ? 'bg-amber-100 text-amber-600' : (notification.notificationType === 'status_updated' || notification.notificationType === 'ticket_closed') ? 'bg-green-100 text-green-600' : 'bg-cream-input text-brand'}`}>
                                                        {isInfo ? (
                                                            <Bell className="h-4 w-4" />
                                                        ) : isWarn ? (
                                                            <AlertCircle className="h-4 w-4" />
                                                        ) : (
                                                            <CheckCircle className="h-4 w-4" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm text-ink">{notification.message}</p>
                                                        <span className="text-xs text-[#8C8175]">{moment(notification.createdDate).fromNow()}</span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        clearNotification(notification._id || notification.id);
                                                    }}
                                                    disabled={markNotificationReadMutation.isLoading}
                                                    className="shrink-0 self-start px-2 text-xs font-medium text-[#8C8175] transition-colors hover:text-brand disabled:opacity-50"
                                                >
                                                    Clear
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                                {dashboardNotifications.length > 3 && (
                                    <div className="pt-1 text-center">
                                        <button
                                            onClick={() => setShowAllNotifications(!showAllNotifications)}
                                            className="text-sm font-semibold text-brand hover:underline"
                                        >
                                            {showAllNotifications ? 'Show Less' : `Show ${dashboardNotifications.length - 3} More`}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-8 text-center">
                                <Bell className="mx-auto mb-3 h-12 w-12 text-[#D8CDBD]" />
                                <p className="text-[#8C8175]">No notifications</p>
                            </div>
                        )}
                    </section>

                    {/* Active Contracts */}
                    <section className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-display text-base font-semibold text-ink">Active Contracts</h2>
                            <RouterLink to={URLS.CUSTOMER.CONTRACT} className="flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
                                View all <ChevronRight className="h-4 w-4" />
                            </RouterLink>
                        </div>

                        {stats.contracts.length > 0 ? (
                            <div className="space-y-4">
                                {stats.contracts.map(contract => (
                                    <div key={contract.id} className="rounded-xl border border-[#ECE3D5] p-4 transition-colors hover:bg-cream">
                                        <div className="flex items-start gap-3">
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cream-input text-brand">
                                                <FileText className="h-5 w-5" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h3 className="font-medium text-ink">{convertToPresentableText(contract.contractType)}</h3>
                                                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${contract.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-cream-100 text-[#6B675F]'}`}>
                                                        {contract.status}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-[#8C8175]">{contract.location}</p>
                                                <p className="mt-1 text-sm text-[#8C8175]">
                                                    {moment(contract.startDate).format('MMM D, YYYY')} - {contract.endDate ? moment(contract.endDate).format('MMM D, YYYY') : `Notice ${contract.noticePeriod} weeks`}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="py-8 text-center">
                                <FileText className="mx-auto mb-3 h-12 w-12 text-[#D8CDBD]" />
                                <p className="text-[#8C8175]">No active contracts</p>
                            </div>
                        )}
                    </section>

                    {/* Support Section */}
                    <section className="rounded-2xl border border-[#ECE3D5] bg-white p-6">
                        <h2 className="mb-4 font-display text-base font-semibold text-ink">Need Help?</h2>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="rounded-xl border border-[#ECE3D5] p-4 text-center">
                                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-cream-input text-brand">
                                    <MessageSquare className="h-5 w-5" />
                                </div>
                                <h3 className="font-medium text-ink">Chat Support</h3>
                                <p className="mb-3 text-sm text-[#8C8175]">Available 24/7 for you</p>
                                <RouterLink
                                    to={URLS.CUSTOMER.SUPPORT}
                                    className="block w-full rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-dark"
                                >
                                    Start Chat
                                </RouterLink>
                            </div>

                            <div className="rounded-xl border border-[#ECE3D5] p-4 text-center">
                                <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-cream-input text-brand">
                                    <LifeBuoy className="h-5 w-5" />
                                </div>
                                <h3 className="font-medium text-ink">Help Center</h3>
                                <p className="mb-3 text-sm text-[#8C8175]">Contact our support team</p>
                                <RouterLink
                                    to={URLS.CUSTOMER.SUPPORT}
                                    className="block w-full rounded-full border border-brand bg-white px-4 py-2 text-sm font-semibold text-brand transition-colors hover:bg-cream-input"
                                >
                                    Visit Help Center
                                </RouterLink>
                            </div>
                        </div>
                    </section>
                </div>

            {/* 1-Time Announcement Modal */}
            <AnnouncementModal />
        </div>
    );
};

export default CustomerDashboard;
